import { Box, Divider, Grid, Stack } from "@mui/material";
import React, { useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { getAssetTypeLabel, managedSurveyAssetType } from "../shared/constants/managedSurveyAssetTypes";
import { AddButton } from "../shared/FormLayout/IconButtons/AddButton";
import { DeleteButton } from "../shared/FormLayout/IconButtons/DeleteButton";
import {
  FloatingLabelInput,
  FloatingLabelInputWithUpdatableStyle,
} from "../shared/FormLayout/InputTemplates/FloatingLabelInput";
import { getSubAssetType, Panel, SurveyableAsset } from "../shared/interfaces/SurveyableAsset";
import LabelText from "../shared/LabelValueFormatters/LabelText";
import ValueText from "../shared/LabelValueFormatters/ValueText";
import { ValidationMessage } from "../shared/ValidationMessage";
import { getNextPanelNo } from "./PanelNumberIncrement";
import { orderPanelsByPanelNumberNumerically } from "./PanelNumberOrdering";
import { validPanelNumber } from "./PanelNumberRegex";
import SwitchgearForm from "./SwitchgearForm";

interface Props {
  switchgear?: SurveyableAsset;
  editable?: boolean;
  onEdit: (switchgear: SurveyableAsset) => void;
  emptyActions?: JSX.Element | undefined;
}

export function SwitchgearDetails(props: Props) {
  const { t } = useTranslation();

  const {
    register,
    formState: { errors },
    setValue,
    clearErrors,
  } = useFormContext();

  const { switchgear, editable, onEdit } = props;

  const subAssetLabel = getSubAssetType(switchgear?.assetType);

  function handleInputChange(event: any, index: number) {
    const name = event.target.name;
    const value = event.target.value;

    if (name === "panelNo") {
      setValue(`switchgearDetails.panel-${index}`, value);
    }

    if (switchgear?.panels) {
      let updatedSwitchgearPanels = [...switchgear?.panels];
      updatedSwitchgearPanels[index] = {
        ...updatedSwitchgearPanels[index],
        [name]: value,
      };
      onEdit({ ...switchgear, panels: updatedSwitchgearPanels });
    }
  }

  function handleAddPanel() {
    if (switchgear) {
      let prepopulatedPanelNo = "1";

      if (switchgear?.panels && switchgear.panels.length > 0) {
        const sortedPanels = orderPanelsByPanelNumberNumerically(switchgear?.panels, "asc");
        const lastPanelNo = sortedPanels.reverse().find((panel) => panel.panelNo !== "")?.panelNo || "";
        prepopulatedPanelNo = getNextPanelNo(lastPanelNo);
      }

      const updatedSwitchgearPanels: Panel[] = [
        ...(switchgear?.panels || []),
        { name: "", panelNo: prepopulatedPanelNo, assetTag: "" },
      ];
      onEdit({ ...switchgear, panels: updatedSwitchgearPanels });
    }
  }

  function handleRemovePanel(index: number) {
    if (switchgear?.panels) {
      let updatedSwitchgearPanels = [...switchgear?.panels];
      updatedSwitchgearPanels.splice(index, 1);
      onEdit({ ...switchgear, panels: updatedSwitchgearPanels });
      clearErrors();
    }
  }

  const panelNumberIsDuplicated = useCallback(
    (newPanelNo: string) => {
      let existingPanelNos = switchgear?.panels?.map((panel) => panel.panelNo);

      if (existingPanelNos && existingPanelNos.filter((panelNo) => panelNo && panelNo === newPanelNo).length > 1) {
        return true;
      } else {
        return false;
      }
    },
    [switchgear?.panels],
  );

  return (
    <div>
      <React.Fragment>
        {switchgear && <SwitchgearForm switchGear={switchgear} onChange={onEdit} />}

        {getAssetTypeLabel(switchgear?.assetType) !== managedSurveyAssetType.Transformer && (
          <>
            {switchgear?.panels && switchgear?.panels.length > 0 ? (
              switchgear?.panels.map((panel: Panel, index: number) => {
                return (
                  <React.Fragment key={`fragment-${index}`}>
                    <Grid container spacing={1} my={1}>
                      <Grid item xs={12} md={3.5} display="flex" justifyContent="flex-start" alignItems="flex-start">
                        {editable ? (
                          <Stack>
                            <FloatingLabelInputWithUpdatableStyle
                              keyName={`input-key-${index}`}
                              value={panel.panelNo}
                              label={t("panels.panelNumber", {
                                val: t(subAssetLabel),
                              })}
                              maxLength={50}
                              inputClassName={
                                errors.switchgearDetails && errors.switchgearDetails[`panel-${index}`]
                                  ? "p-invalid"
                                  : undefined
                              }
                              {...register(`switchgearDetails.panel-${index}`, {
                                value: panel.panelNo,
                                validate: {
                                  notEmpty: (value) => value && value !== "",
                                  notDuplicate: (value) => !panelNumberIsDuplicated(value),
                                  correctPattern: (value) => validPanelNumber(value),
                                },
                              })}
                              name="panelNo"
                              onChange={(event: any) => handleInputChange(event, index)}
                            />
                            {errors.switchgearDetails &&
                              errors.switchgearDetails[`panel-${index}`] &&
                              errors.switchgearDetails[`panel-${index}`].type === "notDuplicate" && (
                                <ValidationMessage
                                  invalidMessage={t("panels.panelDuplication", {
                                    val: t(subAssetLabel),
                                  })}
                                  validationType="error"
                                  showIcon
                                />
                              )}
                            {errors.switchgearDetails &&
                              errors.switchgearDetails[`panel-${index}`] &&
                              errors.switchgearDetails[`panel-${index}`].type === "notEmpty" && (
                                <ValidationMessage
                                  invalidMessage={t("panels.panelNumberRequired", {
                                    val: subAssetLabel,
                                  })}
                                  validationType="error"
                                  showIcon
                                />
                              )}
                            {errors.switchgearDetails &&
                              errors.switchgearDetails[`panel-${index}`] &&
                              errors.switchgearDetails[`panel-${index}`].type === "correctPattern" && (
                                <ValidationMessage
                                  invalidMessage={t("panels.incorrectPanelNumberFormat", { val: subAssetLabel })}
                                  validationType="error"
                                  showIcon
                                />
                              )}
                          </Stack>
                        ) : (
                          <LabelText>{panel.panelNo}</LabelText>
                        )}
                      </Grid>
                      <Grid item xs={12} md={4.5} display="flex" justifyContent="flex-start" alignItems="flex-start">
                        {editable ? (
                          <FloatingLabelInput
                            keyName={`input-value-${index}`}
                            name="name"
                            value={panel.name || ""}
                            label={t("panels.panelName", {
                              val: t(subAssetLabel),
                            })}
                            onChange={(event: any) => handleInputChange(event, index)}
                            maxLength={50}
                          />
                        ) : (
                          <ValueText>{panel?.name}</ValueText>
                        )}
                      </Grid>
                      <Grid item xs={12} md={3} display="flex" justifyContent="flex-start" alignItems="flex-start">
                        <FloatingLabelInput
                          name="assetTag"
                          value={panel.assetTag || ""}
                          label={t("panels.assetTag")}
                          onChange={(event) => handleInputChange(event, index)}
                          maxLength={50}
                          width="100%"
                        />
                      </Grid>
                      {editable && (
                        <Grid item xs={1} display="flex" justifyContent="center" alignItems="flex-start">
                          <DeleteButton
                            key={`deleteButton-${panel.panelNo}`}
                            onClick={() => handleRemovePanel(index)}
                          />
                        </Grid>
                      )}
                    </Grid>
                    <Divider />
                  </React.Fragment>
                );
              })
            ) : (
              <Stack display="flex" justifyContent="center" textAlign="center">
                <LabelText>{t("panels.panelDetailsEmpty", { val: t(subAssetLabel) })}</LabelText>

                {props.emptyActions}
              </Stack>
            )}
          </>
        )}

        {editable && getAssetTypeLabel(switchgear?.assetType) !== managedSurveyAssetType.Transformer && (
          <Box display="flex" justifyContent="center">
            <AddButton onClick={handleAddPanel} />
          </Box>
        )}
      </React.Fragment>
    </div>
  );
}
